<template>
  <AppScreen :title="t('spending.charts.title')">
    <div class="content">
      <SegmentedControl
        :model-value="mode"
        :options="modes"
        :legend="t('spending.charts.mode')"
        hide-legend
        @update:model-value="chooseMode"
      />
      <template v-if="mode === 'month'">
        <MonthSwitcher :month="month" :current="currentMonth" @change="chooseMonth" />
        <ChartsMonth :month="month" />
      </template>
      <ChartsYear v-else :year="year" :current="currentYear" @change="chooseYear" />
    </div>
  </AppScreen>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { monthSchema, yearSchema } from '@molvia/model'
import AppScreen from '@/components/AppScreen.vue'
import ChartsMonth from '@/components/ChartsMonth.vue'
import ChartsYear from '@/components/ChartsYear.vue'
import MonthSwitcher from '@/components/MonthSwitcher.vue'
import SegmentedControl from '@/components/SegmentedControl.vue'
import { useLocalDay } from '@/composables/useLocalDay'

/**
 * «Графики» (MOL-74, MOL-158, MOL-160, handoff MOL-157 03, 04 and 06): «Месяц · Год» on top and,
 * under it, the month or the calendar year — all in the address, moved by `replace`, so a switch is
 * no step «back». No `mode` is the month, no `month` or `year` today's on this phone. The answer of
 * each tab draws itself under the controls, never above them (MOL-138).
 */
export default defineComponent({
  name: 'MoneyChartsView',
  components: { AppScreen, ChartsMonth, ChartsYear, MonthSwitcher, SegmentedControl },
  setup() {
    const { t } = useI18n()
    const route = useRoute()
    const router = useRouter()
    const today = useLocalDay()

    const mode = computed<'month' | 'year'>(() => (route.query.mode === 'year' ? 'year' : 'month'))
    const modes = computed(() => [
      { value: 'month', label: t('spending.charts.mode_month') },
      { value: 'year', label: t('spending.charts.mode_year') },
    ])
    function chooseMode(value: string): void {
      void router.replace({
        query: { ...route.query, mode: value === 'year' ? 'year' : undefined },
      })
    }

    const currentMonth = computed(() => today.value.slice(0, 7))
    /**
     * A month the address names, else this one; one that is not a month is this one too, and so is
     * one still to come — the future is not a month to look at, as on «Деньгах» (adversarial В).
     */
    const month = computed(() => {
      const asked = route.query.month
      return typeof asked === 'string' &&
        monthSchema.safeParse(asked).success &&
        asked <= currentMonth.value
        ? asked
        : currentMonth.value
    })
    function chooseMonth(value: string): void {
      void router.replace({
        query: { ...route.query, month: value === currentMonth.value ? undefined : value },
      })
    }

    const currentYear = computed(() => today.value.slice(0, 4))
    /** A year the address names, else this one; one still to come or not a year is this one too. */
    const year = computed(() => {
      const asked = route.query.year
      return typeof asked === 'string' &&
        yearSchema.safeParse(asked).success &&
        asked <= currentYear.value
        ? asked
        : currentYear.value
    })
    function chooseYear(value: string): void {
      void router.replace({
        query: { ...route.query, year: value === currentYear.value ? undefined : value },
      })
    }

    return {
      t,
      mode,
      modes,
      chooseMode,
      month,
      currentMonth,
      chooseMonth,
      year,
      currentYear,
      chooseYear,
    }
  },
})
</script>

<style scoped lang="scss">
/* The field is `AppScreen`'s 16 (С-23): a padding here made it 32. 8 between the controls and what
   stands under them (Ф-10); the cards of the answer add their own 16, so they stand 24 apart. */
.content {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: var(--space-2);
}
</style>
